import React, { useState, useEffect, useCallback } from 'react';
import { UPLOADS_BASE } from '../api';

export default function HeroSlider({ slides, settings, content = {} }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused]   = useState(false);

  const next = useCallback(() => setCurrent(c => (c + 1) % Math.max(slides.length, 1)), [slides.length]);
  const prev = () => setCurrent(c => (c - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(next, 5500);
    return () => clearInterval(id);
  }, [next, paused, slides.length]);

  if (!slides.length) return (
    <section style={{ minHeight: '100vh', background: 'var(--color-primary)', paddingTop: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14, padding: '0 1.5rem', textAlign: 'center' }}>
        Aucun slide configuré — ajoutez-en depuis le backoffice.
      </div>
    </section>
  );

  const slide    = slides[current];
  const imageUrl = slide.image_path ? `${UPLOADS_BASE}${slide.image_path}` : null;

  return (
    <section
      style={{ position: 'relative', minHeight: '100vh', background: 'var(--color-primary)', paddingTop: 64, overflow: 'hidden' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`
        @keyframes heroFadeIn { from { opacity:0; transform:scale(1.03); } to { opacity:1; transform:scale(1); } }
        @keyframes heroTextIn { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        .hero-img  { animation: heroFadeIn 0.8s ease; }
        .hero-text { animation: heroTextIn 0.6s ease 0.2s both; }
        .slide-dot { width:7px; height:7px; border-radius:50%; border:1px solid rgba(255,255,255,0.4); cursor:pointer; transition:all .3s; background:transparent; }
        .slide-dot.active { background:var(--color-accent); border-color:var(--color-accent); width:22px; border-radius:4px; }
        .hero-nav { background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.15); color:white; width:40px; height:40px; border-radius:50%; cursor:pointer; font-size:18px; display:flex; align-items:center; justify-content:center; transition:all .2s; }
        .hero-nav:hover { background:var(--color-accent); border-color:var(--color-accent); }
        .hero-cta { background:var(--color-accent); color:#fff; padding:12px 28px; border:none; border-radius:3px; font-family:'DM Sans',sans-serif; font-weight:700; font-size:14px; cursor:pointer; text-decoration:none; display:inline-block; transition:background .2s; }
        .hero-cta:hover { background:var(--color-accent-light); }
        .hero-content {
          position: relative; z-index: 2;
          max-width: 1200px; margin: 0 auto;
          padding: 3rem 1.2rem 6rem;
          display: flex; flex-direction: column; justify-content: center;
          min-height: calc(100vh - 64px);
        }
        .hero-title {
          font-family: var(--font-heading, 'Playfair Display', serif);
          font-size: clamp(1.5rem, 5vw, 4rem);
          font-weight: 900;
          color: white;
          line-height: 1.2;
          margin-bottom: 1rem;
          word-break: keep-all;
          overflow-wrap: break-word;
          hyphens: none;
        }
        .hero-subtitle {
          font-size: clamp(13px, 3vw, 16px);
          color: rgba(255,255,255,0.65);
          line-height: 1.8;
          margin-bottom: 2rem;
          max-width: 560px;
          word-break: keep-all;
          overflow-wrap: break-word;
          hyphens: none;
        }
        .hero-legal {
          font-size: 11px;
          color: #fff;
          line-height: 1.7;
          max-width: 600px;
          margin-top: 2rem;
          padding-top: 1.2rem;
          border-top: 1px solid rgba(255,255,255,0.08);
          word-break: keep-all;
          overflow-wrap: break-word;
        }
        @media (min-width:768px) {
          .hero-content { padding: 5rem 2rem 8rem; }
          .hero-nav { width:44px; height:44px; }
        }
      `}</style>

      {/* Image de fond — pleine visibilité */}
      {imageUrl && (
        <div key={current} className="hero-img" style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: 1,
        }} />
      )}

      {/* Overlay léger uniquement pour lisibilité du texte */}
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(to right, rgba(10,22,40,0.72) 0%, rgba(10,22,40,0.35) 60%, rgba(10,22,40,0.1) 100%)' }} />

      {/* Contenu */}
      <div key={`t-${current}`} className="hero-text hero-content">
        <div style={{ display:'inline-flex', alignItems:'center', background:'rgba(15, 138, 144, 0.4)', border:'1px solid rgba(6, 5, 2, 0.3)', padding:'5px 12px', borderRadius:2, marginBottom:'1.2rem', alignSelf:'flex-start' }}>
          <span style={{ fontSize:10, letterSpacing:2, color:'#fff', textTransform:'uppercase', fontWeight:600 }}>
            {content.badge || settings['hero.badge'] || ''}
          </span>
        </div>

        <h1 className="hero-title">{slide.title}</h1>

        {slide.subtitle && <p className="hero-subtitle">{slide.subtitle}</p>}

        {slide.cta_label && (
          <div>
            <a href={slide.cta_url || '#'} className="hero-cta">{slide.cta_label}</a>
          </div>
        )}

        <div className="hero-legal">
          {content.legal || settings['hero.legal'] || ''}
        </div>
      </div>

      {/* Boutons nav — cachés sur très petit écran */}
      {slides.length > 1 && (
        <>
          <button className="hero-nav" onClick={prev} style={{ position:'absolute', left:'1rem', top:'50%', transform:'translateY(-50%)', zIndex:3, display: slides.length > 1 ? 'flex' : 'none' }}>‹</button>
          <button className="hero-nav" onClick={next} style={{ position:'absolute', right:'1rem', top:'50%', transform:'translateY(-50%)', zIndex:3 }}>›</button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div style={{ position:'absolute', bottom:'1.5rem', left:'50%', transform:'translateX(-50%)', zIndex:3, display:'flex', gap:'0.5rem', alignItems:'center' }}>
          {slides.map((_, i) => (
            <button key={i} className={`slide-dot${i===current?' active':''}`} onClick={() => setCurrent(i)} />
          ))}
        </div>
      )}
    </section>
  );
}
